import React, { useEffect, useState } from 'react';

import {
  Container,
  Content,
  Form,
  FormSurface,
  Section,
  SectionTitle,
  SectionBadge,
  SectionBadgeText,
  SectionCard,
  SectionHeader,
  UnitsList,
} from './styles';
import { Header } from '../../../../components/Interface/Headers/Header';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { SearchInput } from '../../../../components/Interface/Forms/Inputs/SearchInput';
import { RFValue } from 'react-native-responsive-fontsize';
import { ParceiroDTO, UnidadeDTO } from '../../../../dtos/ParceiroDTO';
import { NotFound } from '../../../../components/Interface/Search/NotFound';
import { apiMasterClin } from '../../../../services/api';
import { Loading } from '../../../../components/Interface/Loadings/Loading';
import { StoreUnitItem } from '../../../../components/shared/Stores/StoreUnitItem';
import { Titles } from '../../../../components/Interface/Typographies/Titles';
import { Keyboard, TouchableWithoutFeedback } from 'react-native';

interface Params {
  data: ParceiroDTO;
}

interface State {
  id: number;
  uf: string;
  nome: string;
}

export function StoreUnits() {
  const navigation = useNavigation();
  const route = useRoute();
  const screenIsFocus = useIsFocused();

  const { data } = route.params as Params;

  const [unidadesData, setUnidadesData] = useState<UnidadeDTO[]>(
    [] as UnidadeDTO[],
  );

  const [search, setSearch] = useState('');

  const [states, setState] = useState<State[]>([] as State[]);

  const [filteredUnitsBySearchTerm, setFilteredUnitsBySearchTerm] = useState<
    UnidadeDTO[]
  >([] as UnidadeDTO[]);

  useEffect(() => {
    let isMounted = true;

    async function fetchUnits() {
      await apiMasterClin
        .get('/parceiro/' + data.id + '/unidades')
        .then(({ data }) => {
          const { unidades } = data;

          if (unidades) {
            if (isMounted) {
              setUnidadesData(unidades);

              let statesList: State[] = [] as State[];

              unidades.forEach((item: UnidadeDTO) => {
                const checkObjectAlreadyAddedInArray = statesList.find(
                  state => state.id === item.endereco.estado.id,
                );

                if (!checkObjectAlreadyAddedInArray) {
                  statesList.push({
                    id: item.endereco.estado.id,
                    nome: item.endereco.estado.nome,
                    uf: item.endereco.estado.uf,
                  });
                }
              });

              setState(statesList);

              isMounted = false;
            }
          }
        })
        .catch(({ response }) => {
          const { mensagem } = response.data;

          if (mensagem) {
            navigation.navigate('ConfirmationScreen', {
              type: 'error',
              buttonAction: () => navigation.navigate('Initial'),
              buttonText: 'Ir para a tela inicial',
              closeIconAction: () => navigation.navigate('Initial'),
              text: mensagem,
              title: 'Ocorreu um problema',
            });
          } else {
            navigation.navigate('ConfirmationScreen', {
              type: 'error',
              buttonAction: () => navigation.navigate('Initial'),
              buttonText: 'Ir para a tela inicial',
              closeIconAction: () => navigation.navigate('Initial'),
              text: 'Ocorreu um erro no processamento. Entre em contato com nosso suporte para obter ajuda.',
              title: 'Ocorreu um problema',
            });
          }
        });
    }

    fetchUnits();

    return () => {
      isMounted = false;
    };
  }, [screenIsFocus]);

  function filterStoresByState(stateId: number) {
    const filteredStores = unidadesData.filter(
      item => item.endereco.estado.id === stateId,
    );

    if (filteredStores) {
      return filteredStores;
    }

    return [];
  }

  function handleSearchStations(name: string) {
    const filter = unidadesData.filter(
      (e: UnidadeDTO) =>
        e.nome
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .includes(
            name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
          ) ||
        e.endereco.bairro.nome
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .includes(
            name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
          ) ||
        e.endereco.cidade.nome
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .includes(
            name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
          ) ||
        e.endereco.estado.nome
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .includes(
            name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
          ),
    );

    setFilteredUnitsBySearchTerm(filter);
    setSearch(name);
  }

  return unidadesData.length !== 0 ? (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <Container>
        <Header icon="chevron-left" action={() => navigation.goBack()} />

        <Content
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: RFValue(20) }}>
          <Form>
            <FormSurface>
              <Titles
                title="Unidades"
                subtitle="Busque por nome, bairro ou cidade"
              />
              <SearchInput
                placeholder="Pesquise aqui!"
                value={search}
                onChangeText={handleSearchStations}
              />
            </FormSurface>
          </Form>

          {unidadesData.length !== 0 && search.length ? (
            filteredUnitsBySearchTerm.length !== 0 ? (
              <SectionCard>
                {filteredUnitsBySearchTerm.map((item, index) => (
                  <UnitsList key={item.id}>
                    <StoreUnitItem
                      data={item}
                      showDivisor={index !== 0}
                      logoImageUrl={data.imagens.logo}
                    />
                  </UnitsList>
                ))}
              </SectionCard>
            ) : (
              <NotFound />
            )
          ) : (
            states.map(state => (
              <Section key={state.id}>
                <SectionHeader>
                  <SectionTitle>{state.nome}</SectionTitle>
                  <SectionBadge>
                    <SectionBadgeText>
                      {filterStoresByState(state.id).length} unidades
                    </SectionBadgeText>
                  </SectionBadge>
                </SectionHeader>

                {filterStoresByState(state.id).map((item, index) => (
                  <UnitsList key={item.id}>
                    <StoreUnitItem
                      data={item}
                      showDivisor={index !== 0}
                      logoImageUrl={data.imagens.logo}
                    />
                  </UnitsList>
                ))}

              </Section>
            ))
          )}
        </Content>
      </Container>
    </TouchableWithoutFeedback>
  ) : (
    <Loading />
  );
}
